import {merge} from '#core/utils/config-utils.js';

import {PALETTE_IDS} from '#core/application/modules/ids.js';

import {COLOR, FONT} from './theme.js';
import {getBaseComponentsConfig} from './style/base-components';
import {getPageHeadersConfigs} from './style/interface';
import {getStaticPagesConfig} from './style/static-pages';
import {getMatchAccaConfig} from './style/matchacca';
import {getDialogConfig} from './style/dialogs';
import {getMenuListsConfigs} from './style/menus-links-lists';
import {getVirtualsConfig} from './style/virtuals';
import {getRacingConfig} from './style/racing';
import {getBetHistoryConfig} from './style/bethistory';
import {getAccountConfigs} from './style/account';
import {getCardsConfig} from './style/cards';
import {getMarketsConfig} from './style/markets';
import {getBettingCenterConfig} from './style/betting-center';
import {getSportsContentConfigs} from './style/sports-content';

export function getTokens() {
    const TILE_FONT = Object.assign({}, FONT);
    delete TILE_FONT.FAMILY_SECOND;
    const palette = {
        [PALETTE_IDS.FONT]: TILE_FONT,
        [PALETTE_IDS.APP_VIEW]: {
            APPLICATION_PAGE_TEXT: {
                color: COLOR.RESERVED.WHITE,
                fontStretch: 'condensed',
            },
            ACCA_BAR_LABEL_TEXT: {
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
                color: COLOR.BRAND.FOREGROUND1,
            },
        },
        [PALETTE_IDS.MOBILE_NAVBAR]: {
            DOT_NOTIFICATION: {
                DOT_COLOR: COLOR.BRAND.FOREGROUND6,
            },
            ICON_COLOR: COLOR.BRAND.CTA1,
            LABEL_TEXT: {
                color: COLOR.BRAND.BACKGROUND1,
                fontFamily: FONT.FAMILY_SECOND,
                textTransform: 'uppercase',
                fontSize: 8, // according to Zeplin
            },
            CONTAINER_BG: COLOR.BRAND.BACKGROUND7,
        },
        [PALETTE_IDS.SEARCH_RESULT]: {
            SEARCH_RECENT_ITEM_ICON_COLOR: COLOR.BRAND.FOREGROUND7,
            SEARCH_RECENT_ITEM_TEXT: COLOR.BRAND.BACKGROUND6,
            SEARCH_RECENT_ITEM_REMOVE_ICON_COLOR: COLOR.BRAND.BACKGROUND6,

            SEARCH_RESULT_ITEM_ICON_COLOR: COLOR.BRAND.FOREGROUND3,
            SEARCH_RESULT_ITEM_TOURNAMENT_TEXT: COLOR.BRAND.FOREGROUND2,
            SEARCH_RESULT_ITEM_NAME_TEXT: {
                fontWeight: FONT.WEIGHT_BOLD,
                color: COLOR.BRAND.FOREGROUND1,
            },
            SEARCH_RESULT_ITEM_STATUS_TEXT: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.SEARCH]: {
            SEARCH_INPUT: {
                INPUT_BORDER_RADIUS: 20,
                INPUT_PLACEHOLDER_TEXT: COLOR.BRAND.FOREGROUND3,
                INPUT_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
                INPUT_BORDER_COLOR: COLOR.BRAND.FOREGROUND2,
            },
        },
        [PALETTE_IDS.SCOREBOARDS]: {
            SCOREBOARD_CONTAINER_BG: COLOR.BRAND.BACKGROUND6,
            PRIMARY_COLOR: COLOR.BRAND.FOREGROUND6,
            ACCENT_COLOR: COLOR.BRAND.ACCENT1,
            TIMING_STATUS_LABEL: {
                EVENT_TIMING_LIVE_TEXT: COLOR.BRAND.BACKGROUND1,
                EVENT_TIMING_UPCOMING_TEXT: COLOR.BRAND.BACKGROUND1,
            },

            SCORE_TEXT: COLOR.BRAND.ACCENT1,
            SEGMENT_TEXT: COLOR.BRAND.FOREGROUND7,
            SEGMENT_TOTAL_HEADER_TEXT: COLOR.BRAND.ACCENT1,
            SEGMENT_TOTAL_TEXT: {
                color: COLOR.BRAND.ACCENT1,
                fontSize: FONT.SIZE_LARGE,
            },
            SEPARATOR_COLOR: COLOR.BRAND.FOREGROUND6,
        },
        [PALETTE_IDS.SKELETON]: {
            SKELETON_COLOR: COLOR.BRAND.BACKGROUND5,
        },
        [PALETTE_IDS.PROMOTIONS]: {
            BANNER_MARKET_EVENT_NAME_TEXT: {
                fontSize: FONT.SIZE_MEDIUM,
                textTransform: 'uppercase',
            },
            BANNER_MARKET_SPORT_LEAGUE_TEXT: {
                fontSize: FONT.SIZE_SMALL,
            },
            BANNER_MARKET_START_TIME_TEXT: {
                fontSize: FONT.SIZE_SMALL,
            },
        },
        [PALETTE_IDS.CHANGED_VALUE_INDICATOR]: {
            CVI_NEW_VALUE_BORDER_RADIUS: 0,
            CVI_INDICATION_ICON_COLOR: COLOR.BRAND.ACCENT1,
        },
        [PALETTE_IDS.INPUT]: {
            INPUT_BG: COLOR.BRAND.BACKGROUND1,
            INPUT_BORDER_COLOR: `${COLOR.BRAND.FOREGROUND2}|${COLOR.BRAND.FOREGROUND5}`,
            INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.ACCENT3,
            INPUT_DISABLED_BORDER_COLOR: COLOR.BRAND.FOREGROUND6,
            INPUT_TEXT: COLOR.BRAND.FOREGROUND1,
            INPUT_PLACEHOLDER_TEXT: COLOR.BRAND.FOREGROUND6,
            INPUT_DISABLED_TEXT: COLOR.BRAND.FOREGROUND6,
            INPUT_ICON_COLOR: COLOR.BRAND.FOREGROUND1,

            INPUT_ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR1,
            INPUT_LABEL_TEXT: {
                fontSize: FONT.SIZE_SMALL,
                color: `${COLOR.BRAND.FOREGROUND2}|${COLOR.BRAND.FOREGROUND4}`,
            },
        },
        [PALETTE_IDS.STAKE_INPUT]: {
            // Both dark and light variants
            STAKE_INPUT_BG: COLOR.RESERVED.TRANSPARENT,
            STAKE_INPUT_TEXT: `${COLOR.BRAND.FOREGROUND6}|${COLOR.BRAND.FOREGROUND1}`,
            STAKE_INPUT_DISABLED_TEXT: COLOR.BRAND.FOREGROUND3,
            STAKE_INPUT_DISABLED_BORDER_COLOR: `${COLOR.BRAND.FOREGROUND7}|${COLOR.BRAND.FOREGROUND3}`,
            STAKE_INPUT_BORDER_COLOR: `${COLOR.BRAND.FOREGROUND1}|${COLOR.BRAND.FOREGROUND6}`,
            STAKE_INPUT_FOCUSED_BORDER_COLOR: `${COLOR.BRAND.ACCENT3}|${COLOR.BRAND.CTA2}`,
            STAKE_INPUT_ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR1,
        },
        [PALETTE_IDS.RANGE_INPUT]: {
            RANGE_TRACK_COLOR: COLOR.BRAND.BACKGROUND5,
            RANGE_TRACK_DISABLED_COLOR: COLOR.BRAND.BACKGROUND5,
            RANGE_THUMB_COLOR: COLOR.BRAND.FOREGROUND1,
            RANGE_THUMB_DISABLED_COLOR: COLOR.BRAND.FOREGROUND3,
            RANGE_LABEL_TEXT: {
                fontSize: FONT.SIZE_XXSMALL,
                color: COLOR.BRAND.FOREGROUND2,
            },
        },
        [PALETTE_IDS.ERROR_NOTIFICATION]: {
            ERROR_BG: COLOR.SEMANTIC.ERROR2,
            ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR2,
        },
        [PALETTE_IDS.WARNING_NOTIFICATION]: {
            WARNING_BG: COLOR.SEMANTIC.WARNING2,
            WARNING_BORDER_COLOR: COLOR.SEMANTIC.WARNING2,
        },
        [PALETTE_IDS.SUCCESS_NOTIFICATION]: {
            SUCCESS_BG: COLOR.SEMANTIC.SUCCESS2,
            SUCCESS_BORDER_COLOR: COLOR.SEMANTIC.SUCCESS2,
        },
        [PALETTE_IDS.INFO_NOTIFICATION]: {
            INFO_BG: COLOR.BRAND.BACKGROUND5,
            INFO_BORDER_COLOR: COLOR.BRAND.BACKGROUND5,
        },
        [PALETTE_IDS.EMPTY_CONTENT_MESSAGE]: {
            EMPTY_CONTENT_MESSAGE_TEXT: COLOR.BRAND.BACKGROUND7,
        },
        [PALETTE_IDS.SWITCH]: {
            SWITCH_CONTAINER_BG: COLOR.BRAND.BACKGROUND1,
            SWITCH_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND1,
            SWITCH_BUTTON_BG: COLOR.BRAND.FOREGROUND1,
            SWITCH_ICON_COLOR: COLOR.BRAND.FOREGROUND6,
            SWITCH_LABEL_TEXT: `${COLOR.RESERVED.BLACK}|${COLOR.BRAND.BACKGROUND1}`,
            SWITCH_DISABLED_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND7,
            SWITCH_DISABLED_BUTTON_BG: `${COLOR.BRAND.FOREGROUND7}|${COLOR.BRAND.BACKGROUND1}`,
            SWITCH_DISABLED_LABEL_TEXT: COLOR.BRAND.FOREGROUND3,
            SWITCH_DISABLED_CONTAINER_BG: COLOR.BRAND.BACKGROUND1,
        },
    };

    const tokens = merge(
        {
            custom: {},
            palette,
        },
        getBaseComponentsConfig(),
        getAccountConfigs(),
        getMenuListsConfigs(),
        getBetHistoryConfig(),
        getBettingCenterConfig(),
        getCardsConfig(),

        getDialogConfig(),
        getMarketsConfig(),
        getMatchAccaConfig(),
        getPageHeadersConfigs(),
        getRacingConfig(),
        getStaticPagesConfig(),
        getVirtualsConfig(),
        getSportsContentConfigs()
    );

    return tokens;
}
